import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { AuditLogChange } from '../../../../interfaces/auditLog.interface';
import { breakable, showValue } from './detailHelpers';

interface ChangesGridProps {
  changes: AuditLogChange[];
  showOld: boolean;
  showNew: boolean;
}

const BASE = 'auditLogs.list.pageTexts.detail';
const FIELD_COL = 'col-12 col-sm-4';

/** A stored value as a coloured chip; a value that was never set is a plain dash, not a chip with a dash in it. */
const valueChip = (value: any, className: string) => (
  showValue(value) === '-' ? '-' : (
    <span className={clsx('d-inline-block rounded px-2 py-1 text-break', className)}>{showValue(value)}</span>
  )
);

/**
 * The field changes as a Field / Previous / Updated grid. Divs with table roles rather than a <table>, so below the
 * sm breakpoint each field stacks its values under its name and nothing scrolls sideways. Old and new values differ by
 * strike-through and weight as well as by colour.
 */
const ChangesGrid: React.FC<ChangesGridProps> = ({ changes, showOld, showNew }) => {
  const { t } = useTranslation();
  const label = (key: string) => t(`${BASE}.${key}`);
  // One value column takes the room of two when an entry has only old or only new values
  const valueCol = showOld && showNew ? 'col-12 col-sm-4' : 'col-12 col-sm-8';

  return (
    <div role="table" className="border rounded">
      <div role="row" className="row g-0 d-none d-sm-flex audit-panel-bg px-3 py-2 text-heading text-uppercase small fw-semibold">
        <div role="columnheader" className={FIELD_COL}>{label('field')}</div>
        {showOld && <div role="columnheader" className={valueCol}>{label('oldValue')}</div>}
        {showNew && <div role="columnheader" className={valueCol}>{label('newValue')}</div>}
      </div>
      {changes.map((change, index) => (
        <div role="row" key={change.field} data-testid="audit-log-change-row" className={clsx('row g-0 align-items-center px-3 py-2', index > 0 && 'border-top')}>
          <div role="cell" className={clsx(FIELD_COL, 'fw-semibold text-heading font-monospace text-break')}>{breakable(change.field)}</div>
          {showOld && (
            <div role="cell" className={clsx(valueCol, 'text-break')}>
              <span className="d-sm-none small text-heading me-2">{label('oldValue')}</span>
              {valueChip(change.old_label ?? change.old, 'audit-value-old text-decoration-line-through')}
            </div>
          )}
          {showNew && (
            <div role="cell" className={clsx(valueCol, 'text-break')}>
              <span className="d-sm-none small text-heading me-2">{label('newValue')}</span>
              {valueChip(change.new_label ?? change.new, 'audit-value-new fw-semibold')}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ChangesGrid;
